class StoresDirectoryGallery {
  constructor(root) {
    if (root.dataset.storesDirectoryInitialized === 'true') return;

    this.root = root;
    this.dialog = root.querySelector('[data-stores-lightbox]');
    this.track = this.dialog?.querySelector('[data-gallery-track]');
    this.title = this.dialog?.querySelector('.stores-lightbox__title');
    this.counter = this.dialog?.querySelector('[data-gallery-counter]');
    this.closeButton = this.dialog?.querySelector('[data-gallery-close]');
    this.previousButton = this.dialog?.querySelector('[data-gallery-previous]');
    this.nextButton = this.dialog?.querySelector('[data-gallery-next]');
    this.viewport = this.dialog?.querySelector('[data-gallery-viewport]');
    this.stage = this.dialog?.querySelector('.stores-lightbox__stage');
    this.controls = this.dialog?.querySelector('.stores-lightbox__controls');
    this.slides = [];
    this.currentIndex = 0;
    this.trigger = null;
    this.pointerStartX = null;
    this.backToTopButton = root.querySelector('[data-stores-back-to-top]');
    this.topSentinel = root.querySelector('[data-stores-top-sentinel]');
    this.navigationLinks = Array.from(root.querySelectorAll('.stores-navigation__link'));
    this.storeEntries = Array.from(root.querySelectorAll('[data-store-entry]'));

    if (!this.dialog || !this.track) return;
    this.root.dataset.storesDirectoryInitialized = 'true';
    this.bindEvents();
    this.initializeNavigation();
    this.initializeBackToTop();
  }

  bindEvents() {
    this.root.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-gallery-open]');
      if (opener && this.root.contains(opener)) this.open(opener);
    });

    this.closeButton.addEventListener('click', () => this.close());
    this.previousButton.addEventListener('click', () => this.show(this.currentIndex - 1));
    this.nextButton.addEventListener('click', () => this.show(this.currentIndex + 1));

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.show(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.currentIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.show(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.show(this.slides.length - 1);
      }
    });

    this.dialog.addEventListener('close', () => this.reset());
    this.dialog.addEventListener('cancel', () => window.requestAnimationFrame(() => this.reset()));

    this.viewport.addEventListener('pointerdown', (event) => {
      if (event.isPrimary) this.pointerStartX = event.clientX;
    });
    this.viewport.addEventListener('pointerup', (event) => {
      if (this.pointerStartX === null || !event.isPrimary) return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(distance) >= 50) this.show(distance > 0 ? this.currentIndex - 1 : this.currentIndex + 1);
    });
    this.viewport.addEventListener('pointercancel', () => { this.pointerStartX = null; });
    window.addEventListener('resize', () => {
      const currentSlide = this.slides[this.currentIndex];
      if (currentSlide) this.positionControls(currentSlide);
    });
  }

  open(opener) {
    const gallery = opener.closest('[data-store-gallery]');
    const template = gallery?.querySelector('template');
    if (!template) return;

    this.trigger = opener;
    this.track.replaceChildren(template.content.cloneNode(true));
    this.slides = Array.from(this.track.querySelectorAll('[data-gallery-slide]'));
    if (!this.slides.length) return;

    this.title.textContent = gallery.dataset.storeName || 'Store-Galerie';
    this.show(Number.parseInt(opener.dataset.galleryIndex || '0', 10));
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    window.requestAnimationFrame(() => this.positionControls(this.slides[this.currentIndex]));
    this.closeButton.focus();
  }

  show(index) {
    if (!this.slides.length) return;
    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const isCurrent = slideIndex === this.currentIndex;
      slide.hidden = !isCurrent;
      slide.setAttribute('aria-hidden', String(!isCurrent));
      if (isCurrent) this.prepareImage(slide);
    });
    this.counter.textContent = `${this.currentIndex + 1} / ${this.slides.length}`;
    this.previousButton.hidden = this.slides.length < 2;
    this.nextButton.hidden = this.slides.length < 2;
  }

  prepareImage(slide) {
    const image = slide.querySelector('img');
    if (!image) return;

    image.loading = 'eager';
    const markLoaded = () => {
      slide.classList.add('is-loaded');
      window.requestAnimationFrame(() => this.positionControls(slide));
    };
    if (image.complete && image.naturalWidth > 0) markLoaded();
    else {
      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener('error', markLoaded, { once: true });
    }
  }

  positionControls(slide) {
    const image = slide?.querySelector('img');
    if (!image || !this.stage || !this.controls || slide.hidden || !this.dialog.open) return;

    const imageRect = image.getBoundingClientRect();
    const stageRect = this.stage.getBoundingClientRect();
    const preferredTop = imageRect.bottom - stageRect.top + 10;
    const maximumTop = this.stage.clientHeight - this.controls.offsetHeight - 8;
    const top = Math.max(8, Math.min(preferredTop, maximumTop));
    this.stage.style.setProperty('--stores-lightbox-controls-top', `${Math.round(top)}px`);
  }

  initializeNavigation() {
    if (!this.navigationLinks.length || !this.storeEntries.length) return;

    const setActiveLink = (slug) => {
      this.navigationLinks.forEach((link) => {
        const active = link.getAttribute('href') === `#store-${slug}`;
        link.classList.toggle('is-active', active);
        if (active) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    this.navigationLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const slug = link.hash.replace('#store-', '');
        const target = this.storeEntries.find((entry) => entry.dataset.storeSlug === slug);
        if (!target) return;

        event.preventDefault();
        setActiveLink(slug);
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
        window.history.pushState(null, '', link.hash);
      });
    });

    const initialSlug = window.location.hash.startsWith('#store-')
      ? window.location.hash.replace('#store-', '')
      : this.storeEntries[0].dataset.storeSlug;
    setActiveLink(initialSlug);

    if (!('IntersectionObserver' in window)) return;
    this.navigationObserver = new IntersectionObserver((entries) => {
      const visibleEntries = entries
        .filter((entry) => entry.isIntersecting)
        .sort((first, second) => Math.abs(first.boundingClientRect.top) - Math.abs(second.boundingClientRect.top));
      if (visibleEntries[0]) setActiveLink(visibleEntries[0].target.dataset.storeSlug);
    }, { rootMargin: '-12% 0px -68% 0px', threshold: 0 });
    this.storeEntries.forEach((entry) => this.navigationObserver.observe(entry));
  }

  initializeBackToTop() {
    if (!this.backToTopButton || !this.topSentinel) return;

    this.backToTopButton.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });

    if (!('IntersectionObserver' in window)) {
      this.backToTopButton.classList.add('is-visible');
      return;
    }

    this.topObserver = new IntersectionObserver(([entry]) => {
      this.backToTopButton.classList.toggle('is-visible', !entry.isIntersecting);
    }, { threshold: 0 });
    this.topObserver.observe(this.topSentinel);
  }

  close() {
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else {
      this.dialog.removeAttribute('open');
      this.reset();
    }
  }

  reset() {
    this.track.replaceChildren();
    this.stage?.style.removeProperty('--stores-lightbox-controls-top');
    this.slides = [];
    const focusTarget = this.trigger;
    this.trigger = null;
    if (focusTarget?.isConnected) focusTarget.focus();
  }
}

const initializeStoresDirectory = (scope = document) => {
  scope.querySelectorAll('[data-stores-directory]').forEach((root) => new StoresDirectoryGallery(root));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeStoresDirectory());
} else {
  initializeStoresDirectory();
}

document.addEventListener('shopify:section:load', (event) => initializeStoresDirectory(event.target));
